import React, { useEffect, useState, useRef } from 'react';
import { useTutorial } from '../../context/TutorialContext';

export const TutorialBubble: React.FC = () => {
  const {
    isActive,
    activeTutorial,
    currentStep,
    getCurrentStep,
    nextStep,
    previousStep,
    skipTutorial,
  } = useTutorial();

  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [arrowPosition, setArrowPosition] = useState<'top' | 'bottom' | 'left' | 'right' | 'none'>('none');
  const bubbleRef = useRef<HTMLDivElement>(null);

  const step = getCurrentStep();

  useEffect(() => {
    if (!isActive || !step) return;

    const updatePosition = () => {
      if (!step.target) {
        // Center of screen
        setPosition({
          top: window.innerHeight / 2,
          left: window.innerWidth / 2,
        });
        setArrowPosition('none');
        return;
      }

      // Find target element
      const targetElement = document.querySelector(step.target);
      if (!targetElement) {
        // Fallback to center if target not found
        setPosition({
          top: window.innerHeight / 2,
          left: window.innerWidth / 2,
        });
        setArrowPosition('none');
        return;
      }

      const targetRect = targetElement.getBoundingClientRect();
      const bubbleWidth = 320;
      const bubbleHeight = 200;
      const offset = 20;

      let top = 0;
      let left = 0;
      let arrow: 'top' | 'bottom' | 'left' | 'right' | 'none' = 'none';

      switch (step.position || 'bottom') {
        case 'top':
          top = targetRect.top - bubbleHeight - offset;
          left = targetRect.left + targetRect.width / 2;
          arrow = 'bottom';
          break;
        case 'bottom':
          top = targetRect.bottom + offset;
          left = targetRect.left + targetRect.width / 2;
          arrow = 'top';
          break;
        case 'left':
          top = targetRect.top + targetRect.height / 2;
          left = targetRect.left - bubbleWidth - offset;
          arrow = 'right';
          break;
        case 'right':
          top = targetRect.top + targetRect.height / 2;
          left = targetRect.right + offset;
          arrow = 'left';
          break;
        case 'center':
          top = window.innerHeight / 2;
          left = window.innerWidth / 2;
          arrow = 'none';
          break;
      }

      // Ensure bubble stays within viewport
      top = Math.max(offset, Math.min(top, window.innerHeight - bubbleHeight - offset));
      left = Math.max(offset, Math.min(left, window.innerWidth - bubbleWidth - offset));

      setPosition({ top, left });
      setArrowPosition(arrow);
    };

    // Update position immediately and on resize/scroll
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isActive, step]);

  // Highlight target element
  useEffect(() => {
    if (!isActive || !step?.target || !step.highlightTarget) return;

    const targetElement = document.querySelector(step.target);
    if (!targetElement) return;

    // Add highlight class
    targetElement.classList.add('tutorial-highlight');

    return () => {
      targetElement.classList.remove('tutorial-highlight');
    };
  }, [isActive, step]);

  if (!isActive || !step || !activeTutorial) return null;

  const totalSteps = activeTutorial.steps.length;
  const stepNumber = currentStep + 1;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-[9998] ${
          step.allowInteraction ? 'pointer-events-none bg-opacity-0' : 'bg-opacity-50'
        }`}
        onClick={step.allowInteraction ? undefined : skipTutorial}
      />

      {/* Tutorial Bubble */}
      <div
        ref={bubbleRef}
        className="fixed z-[9999] transform -translate-x-1/2 -translate-y-1/2"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
      >
        {/* Arrow */}
        {arrowPosition !== 'none' && (
          <div
            className={`absolute w-0 h-0 border-solid ${
              arrowPosition === 'top'
                ? 'border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-b-[12px] border-b-white dark:border-b-slate-800 -top-3 left-1/2 -translate-x-1/2'
                : arrowPosition === 'bottom'
                ? 'border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-t-[12px] border-t-white dark:border-t-slate-800 -bottom-3 left-1/2 -translate-x-1/2'
                : arrowPosition === 'left'
                ? 'border-t-[12px] border-t-transparent border-b-[12px] border-b-transparent border-r-[12px] border-r-white dark:border-r-slate-800 -left-3 top-1/2 -translate-y-1/2'
                : 'border-t-[12px] border-t-transparent border-b-[12px] border-b-transparent border-l-[12px] border-l-white dark:border-l-slate-800 -right-3 top-1/2 -translate-y-1/2'
            }`}
          />
        )}

        {/* Bubble Content */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border-2 border-primary-500 dark:border-primary-600 p-6 w-80 max-w-[90vw]">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-primary-600 dark:text-primary-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  Step {stepNumber} of {totalSteps}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {step.title}
              </h3>
            </div>
            <button
              onClick={skipTutorial}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              title="Skip tutorial"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Description */}
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-6">{step.description}</p>

          {/* Progress Bar */}
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-1.5 mb-4">
            <div
              className="bg-primary-500 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${(stepNumber / totalSteps) * 100}%` }}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={previousStep}
              disabled={currentStep === 0}
              className={`text-sm font-medium transition-colors ${
                currentStep === 0
                  ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              }`}
            >
              ← Previous
            </button>
            <button
              onClick={nextStep}
              className="btn-primary text-sm px-6 py-2 flex items-center space-x-2"
            >
              <span>{stepNumber === totalSteps ? 'Finish' : 'Next'}</span>
              {stepNumber < totalSteps && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Inject CSS for highlight effect */}
      <style>{`
        .tutorial-highlight {
          position: relative;
          z-index: 9997 !important;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.5), 0 0 0 99999px rgba(0, 0, 0, 0.5) !important;
          border-radius: 8px;
        }
      `}</style>
    </>
  );
};



